import { useQuery } from "@apollo/client";
import { useMemo, useRef } from "react";
import Button from "@mui/material/Button";
import { Grid, Grid2, IconButton } from "@mui/material";
import { FaLightbulb } from "react-icons/fa6";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import { HiArrowSmallRight, HiOutlineBuildingStorefront } from "react-icons/hi2";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import MaButton from "../../../../Components/UI/ma-button";
import { TfiDownload } from "react-icons/tfi";
import icon1 from "../../../../assets/Icons/dashboard/product.svg";
import icon2 from "../../../../assets/Icons/dashboard/orders.svg";
import icon3 from "../../../../assets/Icons/dashboard/invoice.svg";
import icon4 from "../../../../assets/Icons/dashboard/rating.svg";
import { GET_PROFILE, GET_SELLER_STATS } from "../../../../Utils/Queries";
import {
  getDisplayName,
  getProfileFromQuery,
  getStoreFromQuery,
  hasProfileValue,
} from "../../../utils/get-profile";
import { StatCard } from "./stat-card";
import { RevenueChartCard } from "./revenue-chart";
import { OrderStatusCard } from "./order-status";
import RecentOrdersCard from "./recent-order";
import { TopProductsCard } from "./top-product";
import Loader from "../../../../Components/Common/Loader";
import { TbFileDescription } from "react-icons/tb";

const STORE_TODO_DEFS = [
  {
    key: "storeName",
    isPending: (s) => !hasProfileValue(s?.storeName),
    title: "Add a store name",
    description: "Give your store a name buyers can recognize.",
    icon: <HiOutlineBuildingStorefront />,
    route: "/seller/settings?section=store-name",
  },
  {
    key: "storeLogo",
    isPending: (s) => !hasProfileValue(s?.storeLogo),
    title: "Upload your store logo",
    description: "Make your storefront pop with a recognizable logo.",
    icon: <HiOutlineBuildingStorefront />,
    route: "/seller/settings?section=store-logo",
  },
  {
    key: "storeBannerUrl",
    isPending: (s) => !hasProfileValue(s?.storeBannerUrl),
    title: "Upload your store banner",
    description: "Add a banner image for the top of your public storefront.",
    icon: <HiOutlineBuildingStorefront />,
    route: "/seller/settings?section=store-banner",
  },
  {
    key: "storeDescription",
    isPending: (s) => !hasProfileValue(s?.storeDescription),
    title: "Add a store description",
    description: "Tell buyers what your store is all about.",
    icon: <TbFileDescription />,
    route: "/seller/settings?section=store-description",
  },
  {
    key: "categoriesToSell",
    isPending: (s) => !hasProfileValue(s?.categoriesToSell),
    title: "Pick your categories",
    description: "Choose categories so the right shoppers find you.",
    icon: "📦",
    route: "/seller/settings?section=categories",
  },
  {
    key: "address",
    isPending: (s) => !hasProfileValue(s?.address),
    title: "Add a business address",
    description: "Add an address so we can ship and verify your orders.",
    icon: "📍",
    route: "/seller/settings?section=address",
  },
  {
    key: "registeredNumber",
    isPending: (s) => !hasProfileValue(s?.registeredNumber),
    title: "Add registration number",
    description: "Add your business registration number.",
    icon: "📑",
    route: "/seller/settings?section=registration",
  },
  {
    key: "employerIdentificationNumber",
    isPending: (s) => !hasProfileValue(s?.employerIdentificationNumber),
    title: "Add Employer ID number",
    description: "Add your EIN to complete your tax info.",
    icon: "🪪",
    route: "/seller/settings?section=ein",
  },
];

const STAT_CARD_DEFS = [
  {
    label: "Total Products",
    field: "totalProducts",
    format: (v) => (v == null ? "—" : String(v)),
    icon: icon1,
    iconBg: "bg-[#EAF2FF]",
  },
  {
    label: "Active Orders",
    field: "activeOrders",
    format: (v) => (v == null ? "—" : String(v)),
    icon: icon2,
    iconBg: "bg-[#E6F6EC]",
  },
  {
    label: "Total Revenue",
    field: "totalRevenue",
    format: (v) =>
      v == null || Number.isNaN(Number(v))
        ? "—"
        : `$${Number(v).toLocaleString("en-US", {
            maximumFractionDigits: 0,
          })}`,
    icon: icon3,
    iconBg: "bg-[#F3F4F6]",
  },
  {
    label: "Avg. Rating",
    field: "averageRating",
    format: (v) =>
      v == null || Number.isNaN(Number(v)) ? "—" : Number(v).toFixed(1),
    icon: icon4,
    iconBg: "bg-[#FDECEC]",
  },
];

const Dashboard = () => {
  const { data, loading } = useQuery(GET_SELLER_STATS);
  const stats = data?.getSellerStats;
  const { data: profileData, loading: profile_loading } = useQuery(GET_PROFILE, {
    fetchPolicy: "cache-and-network",
  });

  const userProfile = getProfileFromQuery(profileData);
  const store = getStoreFromQuery(profileData);
  const welcomeName = getDisplayName(userProfile);

  const statCards = STAT_CARD_DEFS.map(
    ({ field, format, label, icon, iconBg }) => ({
      label,
      icon,
      iconBg,
      value: loading ? "—" : format(stats?.[field]),
    }),
  );

  const pendingTodos = useMemo(
    () => STORE_TODO_DEFS.filter((t) => t.isPending(store)),
    [store],
  );
  const completedCount = STORE_TODO_DEFS.length - pendingTodos.length;

  console.log(profileData)

  return (
    <>
      {profile_loading && <Loader />}
      <div className="pb-10">
        <div className="flex items-center mt-10 justify-between mb-5">
          <div>
            <h1 className="text-[22px] font-semibold text-[#151515] my-0">
              Dashboard
            </h1>
            <p className="text-[13px] text-[#626262] my-0 mt-1">
              Welcome back, {welcomeName}{" "}
              <span role="img" aria-label="wave">
                👋
              </span>
            </p>
          </div>
          <Button
            variant="outlined"
            startIcon={<TfiDownload />}
            sx={{
              textTransform: "none",
              color: "#151515",
              borderColor: "#EAEAEA80",
              bgcolor: "#fff",
              borderRadius: "12px",
              px: 2.5,
              py: 1,
              fontWeight: 600,
              "&:hover": {
                borderColor: "#D1D5DB",
                bgcolor: "#F9FAFB",
              },
            }}
          >
            Export
          </Button>
        </div>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item md={12} xs={12}>
            <div className="rounded-[12px] border border-[#EFEFEF] overflow-hidden grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 min-w-0 ">
              {statCards.map((card, idx) => (
                <div className="w-full box-border" key={idx}>
                  <StatCard idx={idx} {...card} />
                </div>
              ))}
            </div>
          </Grid>

          {pendingTodos.length > 0 && (
            <Grid item md={12} sx={{ minWidth: 0, maxWidth: "100%" }}>
              <div className="w-full min-w-0 max-w-full overflow-hidden">
                <TodoSection
                  todos={pendingTodos}
                  completedCount={completedCount}
                  totalCount={STORE_TODO_DEFS.length}
                />
              </div>
            </Grid>
          )}

          {/* <Grid item md={12}>
            <div>
              <ApprovalBanner />
            </div>
          </Grid> */}

          <Grid item md={8} xs={12}>
            <RevenueChartCard />
          </Grid>
          <Grid item md={4} xs={12}>
            <OrderStatusCard />
          </Grid>
          <Grid item md={7} xs={12}>
            <div>
              <RecentOrdersCard />
            </div>
          </Grid>

          <Grid item md={5} xs={12}>
            <TopProductsCard />
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default Dashboard;

const EASE = [0.22, 1, 0.36, 1];

const TodoSection = ({ todos, completedCount, totalCount }) => {
  const scrollerRef = useRef(null);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardWidth = el.firstChild?.getBoundingClientRect().width || 280;
    el.scrollBy({ left: dir * (cardWidth + 16), behavior: "smooth" });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: EASE }}
      className="bg-white border border-solid border-[#EFEFEF] rounded-[14px] p-4 sm:p-5 min-w-0 max-w-full overflow-hidden"
    >
      <div className="flex items-center justify-between mb-4">
        <p className="my-0 text-[14px] font-semibold text-[#151515]">
          To-do{" "}
          <span className="text-[12px] text-[#9CA3AF] font-medium">
            {completedCount}/{totalCount}
          </span>
        </p>
        {todos.length > 1 && (
          <div className="flex items-center gap-2">
            <IconButton
              size="small"
              onClick={() => scrollBy(-1)}
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "999px",
                width: 30,
                height: 30,
                color: "#6B7280",
              }}
            >
              <IoChevronBack size={14} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => scrollBy(1)}
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "999px",
                width: 30,
                height: 30,
                color: "#6B7280",
              }}
            >
              <IoChevronForward size={14} />
            </IconButton>
          </div>
        )}
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-1 -mx-1 px-1 w-full min-w-0 max-w-full [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {todos.map((todo, idx) => (
          <TodoCard key={todo.key} todo={todo} index={idx} />
        ))}
      </div>
    </motion.div>
  );
};

const TodoCard = ({ todo, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, ease: EASE, delay: index * 0.06 }}
    className="snap-start shrink-0 w-[260px] sm:w-[300px] bg-[#F9F9F9] border border-solid border-[#EAEAEA80] rounded-[16px] p-4 hover:shadow-[0_2px_8px_rgba(15,23,42,0.06)] transition-shadow"
  >
    <div className="text-[28px] leading-none mb-3" role="img" aria-label="">
      {todo.icon}
    </div>
    <p className="my-0 text-[14px] font-black text-[#3D3D3D]">
      {todo.title}
    </p>
    <p className="my-0 mt-1 text-[12px] text-[#626262] leading-snug min-h-[32px]">
      {todo.description}
    </p>
    <Link
      to={todo.route}
      className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-[#151515] hover:text-[#2E1834] no-underline"
    >
      Start <HiArrowSmallRight className="text-[16px]" />
    </Link>
  </motion.div>
);

const ApprovalBanner = () => (
  <div className="bg-[#F0FAE5] border border-[#E5E7EB80] border-solid rounded-[14px] px-4 py-3 flex items-center justify-between gap-4">
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 flex items-center justify-center shrink-0 mt-[2px]">
        <FaLightbulb className="text-[18px] text-amber-500" />
      </div>
      <div>
        <p className="text-[12px] font-[600] text-[#3D3D3D] my-0">
          2 Products Awaiting Approval
        </p>
        <p className="text-[12px] mt-1 text-[#626262] ">
          Your recently listed "Ankara Print Tote Bag" and "Hand-carved Jewelry
          Box" are under admin review.
        </p>
      </div>
    </div>
    <MaButton
      title="View Status"
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: "12px",
        border: "1px solid #E5E4E2",
        color: "#6B6966",
      }}
    />
  </div>
);
